/**
 * 收件箱通知 API 服务层
 *
 * 修复消息审查#9：整删 mock 双轨（本域曾按 VITE_USE_MOCK 在内存假数据与真实 HTTP 间切换），
 * 收敛为直连真实后端。历史 mock/notifications.ts 已随之下线，dev/prod/test 均走真实链路。
 */
import type {
  MessageType,
  MessagePreference,
  NotificationListResponse,
  UnreadCountResponse,
  ExpiringSoonResponse,
  PreferencesResponse,
} from '@/types/notifications'
import type { ApiResponse } from '@/services/types'
import { httpInboxNotificationsService } from '@/services/http/HttpInboxNotificationsService'

// ===== IInboxNotificationsService 接口定义 =====
export interface IInboxNotificationsService {
  fetchNotifications(params: {
    page?: number
    pageSize?: number
    filter?: string
    type?: string
    keyword?: string
    signal?: AbortSignal
  }): Promise<ApiResponse<NotificationListResponse>>

  markAsRead(id: string): Promise<ApiResponse<null>>
  markAllAsRead(): Promise<ApiResponse<{ marked: number }>>
  batchMarkRead(ids: string[]): Promise<ApiResponse<{ marked: number }>>

  deleteNotification(id: string): Promise<ApiResponse<null>>
  batchDelete(ids: string[]): Promise<ApiResponse<{ deleted: number; failedIds: string[] }>>

  toggleStar(id: string, starred: boolean): Promise<ApiResponse<null>>
  recordAnnouncementRead(announcementId: string): Promise<ApiResponse<null>>

  fetchUnreadCount(): Promise<ApiResponse<UnreadCountResponse>>
  fetchExpiringSoon(): Promise<ApiResponse<ExpiringSoonResponse>>

  fetchPreferences(): Promise<ApiResponse<PreferencesResponse>>
  updatePreference(type: MessageType, patch: Partial<MessagePreference>): Promise<ApiResponse<MessagePreference>>
  batchUpdatePreferences(prefs: Array<{ type: MessageType } & Partial<MessagePreference>>): Promise<ApiResponse<null>>
}

// 直连真实 HTTP 实现（本域不再有 Mock 分支）
const service: IInboxNotificationsService = httpInboxNotificationsService

// 具名导出（Store 消费）
export function fetchNotifications(params: Parameters<IInboxNotificationsService['fetchNotifications']>[0]) {
  return service.fetchNotifications(params)
}
export function markAsRead(id: string) { return service.markAsRead(id) }
export function markAllAsRead() { return service.markAllAsRead() }
export function batchMarkRead(ids: string[]) { return service.batchMarkRead(ids) }
export function deleteNotification(id: string) { return service.deleteNotification(id) }
export function batchDelete(ids: string[]) { return service.batchDelete(ids) }
export function toggleStar(id: string, starred: boolean) { return service.toggleStar(id, starred) }
export function recordAnnouncementRead(announcementId: string) { return service.recordAnnouncementRead(announcementId) }
export function fetchUnreadCount() { return service.fetchUnreadCount() }
export function fetchExpiringSoon() { return service.fetchExpiringSoon() }
export function fetchPreferences() { return service.fetchPreferences() }
export function updatePreference(type: MessageType, patch: Partial<MessagePreference>) { return service.updatePreference(type, patch) }
export function batchUpdatePreferences(prefs: Array<{ type: MessageType } & Partial<MessagePreference>>) { return service.batchUpdatePreferences(prefs) }
